import React from 'react';

import {
	Bank,
	CredentialType,
	TransactionsLabel,
	TransactionStatus,
	TransactionType,
	TransactionTypeAliasAuthenticationMessageOperationInputDto,
} from '@api/ecommerce-xp';
import { CoreHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { useShowHide, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { ColorPaletteThemeModel, useCountdown, useTheme } from '@bcp-nextgen-dx-component-factory/design-system';
import { useLogger, useSubscription } from '@hooks';
import { modals } from '@modals';
import { useNavigation } from '@navigation';
import { useFetchAuthenticatorMessage, useFetchAuthenticatorType } from '@services';
import { usePTCMetadata, useUserInput } from '@store';
import { Anything } from '@types';

interface AuthenticatorOutput {
	resources: AuthenticatorFooterResources;
	colorPalette: ColorPaletteThemeModel;
	canRequestNewCode: boolean;
	showAuthentication: boolean;
	buttonDisableValidate: boolean;
	showMessageError: boolean;
	authenticationTypeLoading: boolean;
	authenticationMessageLoading: boolean;
	onClickToNavigate: () => void;
	onButtonClick: () => void;
	clearInformation: () => void;
	requestAuthentication: () => void;
	setToken: (value: string) => void;
	setButtonDisableValidate: (value: boolean) => void;
	onChangeValue: (value: string, position: string[] | undefined) => void;
}

interface AuthenticatorFooterResources {
	linkBack: string;
	title: string;
	subtitle: string;
	typeAuthenticationSMS: string;
	authenticationTitle: string;
	description: string;
	message: string;
	askAnotherCode: string;
	validateButton: string;
	errorMessage: string;
}

export const useAuthenticatorHelper = (): AuthenticatorOutput => {
	const t = useTranslation();
	const logger = useLogger();
	const { colorPalette } = useTheme();
	const { back, conclusion, navigate } = useNavigation();
	const { hide, show } = useShowHide(modals.validation);

	const [showAuthentication, setShowAuthentication] = React.useState<boolean>(false);
	const [buttonDisableValidate, setButtonDisableValidate] = React.useState<boolean>(true);
	const [showMessageError, setShowMessageError] = React.useState<boolean>(false);
	const [token, setToken] = React.useState<string>('');
	const [authenticationTypeLoading, setAuthenticationTypeLoading] = React.useState(true);
	const [authenticationMessageLoading, setAuthenticationMessageLoading] = React.useState(false);
	const { languageCode } = usePTCMetadata();
	const { selectedAccount } = useUserInput();
	const { fetch: fetchAuthenticatorMessage } = useFetchAuthenticatorMessage();
	const { subscription } = useSubscription({ token: token });

	const { success } = useFetchAuthenticatorType();

	React.useEffect(() => {
		// hide loading when ending fetch of authenticator type
		if (success !== undefined) {
			setAuthenticationTypeLoading(false);
		}
	}, [success]);

	const labelFromPhone = React.useRef<string>('');
	const waitingTime = React.useMemo(() => {
		const wTime = parseInt(t('placeholders.authenticatorFooter.authenticator.waitingTime'));
		return !isNaN(wTime) ? wTime : 60;
	}, [t]);

	const { timer$, time, canResetCountdown: canRequestNewCode } = useCountdown(waitingTime);

	const resources: AuthenticatorFooterResources = {
		authenticationTitle: t('placeholders.authenticatorFooter.authenticator.title'),
		description: t('placeholders.authenticatorFooter.authenticator.description', {
			phone: labelFromPhone.current,
		}),
		message: t('placeholders.authenticatorFooter.authenticator.timeUntilToAskAnotherCode', {
			time,
		}),
		askAnotherCode: t('placeholders.authenticatorFooter.authenticator.askForAnotherCode'),
		linkBack: t('general.back'),
		title: t('placeholders.authenticatorFooter.footer.title'),
		subtitle: t('placeholders.authenticatorFooter.footer.subtitle'),
		typeAuthenticationSMS: t('placeholders.authenticatorFooter.footer.typeAuthenticationSMS'),
		validateButton: t('general.validate'),
		errorMessage: t('placeholders.authenticatorFooter.authenticator.errorMessage'),
	};

	const restartTime = () => {
		timer$.next({ value: waitingTime });
	};

	const requestAuthentication = () => {
		const transactionType: TransactionType = TransactionType.ECommerceSubscription;
		const transactionLabels: TransactionsLabel[] | null = null;

		const data: TransactionTypeAliasAuthenticationMessageOperationInputDto = {
			accountId: selectedAccount?.account.id,
			bank: Bank.MillenniumBcp,
			credentialType: CredentialType.Sms,
			language: languageCode,
			transaction: transactionType,
			transactionLabels: transactionLabels,
			transactionAmount: 0,
			transactionCurrency: 'EUR',
		};
		setAuthenticationMessageLoading(true);
		fetchAuthenticatorMessage(data).then((res) => {
			if (!CoreHelper.isNullOrEmpty(res.data?.labelToDisplay)) {
				labelFromPhone.current = res.data?.labelToDisplay as string;
				restartTime();
				setShowAuthentication(true);
				setAuthenticationMessageLoading(false);
			}
		});
	};

	const onButtonClick = React.useCallback(() => {
		show();
		logger('validateCode > Code is', { token: token });
		subscription().then((resp: Anything) => {
			hide();
			/* cspell: disable */
			if (
				resp.data?.transactionStatus === TransactionStatus.CODEAUTHEXPIRE ||
				resp.data?.transactionStatus === TransactionStatus.CODEAUTHINVALID ||
				resp.data?.transactionStatus === TransactionStatus.INVALIDCREDENTIALNORETRY
			) {
				setShowMessageError(true);
			} else if (
				resp.data?.transactionStatus === TransactionStatus.TOSTOREANDFOWARDFORM ||
				resp.data?.transactionStatus === TransactionStatus.PROCESSING
			) {
				navigate('serviceError');
			} else {
				conclusion();
			}
			/* cspell: enable */
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token]);

	const clearInformation = React.useCallback(() => {
		restartTime();
		setShowAuthentication(false);
		setToken('');
		setButtonDisableValidate(true);
		setShowMessageError(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onChangeValue = React.useCallback((_v: string, position: string[] | undefined) => {
		const disabledButton =
			position !== undefined
				? position.some((value, index) => {
						if (value === undefined || value === '') {
							logger('some Position', {
								position: index,
								value: value,
								isUndefined: value === undefined,
								isEmpty: value === '',
							});
							return true;
						} else {
							return false;
						}
				  })
				: true;
		setButtonDisableValidate(disabledButton);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		resources,
		colorPalette,
		canRequestNewCode,
		showAuthentication,
		buttonDisableValidate,
		showMessageError,
		authenticationTypeLoading,
		authenticationMessageLoading,
		onClickToNavigate: back,
		onButtonClick,
		clearInformation,
		requestAuthentication,
		setToken,
		setButtonDisableValidate,
		onChangeValue,
	};
};
